import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { prettyBytes } from '@Sage/ssm-utils';

import { getOverrides, useClasses } from '../../../../utils/overrides';
import { createUseStyles, useTheme } from '../../../../utils/styles';

import Icon from '../../../general/Icon';
import Loader from '../../../general/Loader';
import Markdown from '../../../general/Markdown';
import Tooltip from '../../../utils/Tooltip';
import Text from '../../../typography/Text';
import InputWrapper from '../../components/InputWrapper';

import { useThrottle } from '../../../../utils/hooks/useThrottle';
import {
    FILE_EXTENSIONS,
    FILE_TYPES,
    FILE_ITEM_WIDTH,
    FILE_ITEM_GAP,
    checkBrowserCanRender,
    getFileIconColor,
    getFileNameAndExtension,
    convertAcceptToDropzoneFormat,
} from '../shared';
import styles from './styles';

const useStyles = createUseStyles(styles, 'AttachmentList');

const AttachmentList = ({
    accept,
    classes: classesProp,
    className,
    deleteTooltip,
    disabled = false,
    downloadTooltip,
    error,
    files = [],
    handleDownload,
    hint,
    id,
    info,
    isFullWidth,
    isReadOnly = false,
    isRequired,
    isUploading = false,
    label,
    labelMode = 'vertical',
    maxFiles,
    maxSize,
    minSize,
    multiple,
    name,
    onDrop,
    onExceedFileLimitDrop,
    onRemove,
    overrides: overridesProp,
    previewImages,
    showIcon = true,
    title = 'Drag & drop files here',
}) => {
    const classes = useClasses(useStyles, classesProp);
    const theme = useTheme();
    const override = getOverrides(overridesProp, AttachmentList.overrides);

    const scrollContainerRef = useRef(null);
    const dropZoneContainerRef = useRef(null);
    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [canGoPrev, setCanGoPrev] = useState(false);
    const [canGoNext, setCanGoNext] = useState(false);

    // ========================================
    // DERIVED STATE & MEMOIZATION
    // ========================================
    const contentWidth = useMemo(() => {
        const filesWidth = files.length * FILE_ITEM_WIDTH;
        const gapsWidth = (files.length - 1) * FILE_ITEM_GAP;
        return filesWidth + (gapsWidth > 0 ? gapsWidth : 0);
    }, [files.length]);

    const processedFiles = useMemo(
        () =>
            files.map((file) => {
                const fileType = FILE_TYPES[file.type] || FILE_EXTENSIONS[file.type] || 'file';
                const { ext, name: fileName } = getFileNameAndExtension(file.name);

                let preview = file.preview;
                if (previewImages && fileType === 'img' && !preview) {
                    if (file instanceof File || file instanceof Blob) {
                        preview = URL.createObjectURL(file);
                    } else if (typeof file === 'string') {
                        preview = file;
                    }
                }

                return {
                    ...file,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    preview,
                    ext,
                    fileName,
                    fileType,
                    originalFile: file,
                };
            }),
        [files, previewImages],
    );

    // Cleanup object URLs to avoid memory leaks
    useEffect(() => {
        return () => {
            processedFiles.forEach((file) => {
                if (file.preview && file.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, [processedFiles]);

    const markdownOverrides = useMemo(
        () => ({
            p: {
                color:
                    disabled || isReadOnly ? theme.colors.grey[400] : theme.colors.actionMinor[500],
                type: 'body',
                medium: true,
            },
        }),
        [disabled, isReadOnly, theme.colors.actionMinor, theme.colors.grey],
    );

    const rootClassName = classNames(classes.root, className, {
        [classes.error]: error,
        [classes.isFullWidth]: isFullWidth,
        [classes.disabled]: disabled,
        [classes.isReadOnly]: isReadOnly,
    });

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const handleDrop = useCallback(
        (acceptedFiles) => {
            if (acceptedFiles?.length > 0) {
                const hasFileLimit = maxFiles !== undefined && maxFiles > 0;

                // Check if adding files would exceed the limit
                if (hasFileLimit) {
                    const isExceeded =
                        (files?.length || 0) + (acceptedFiles?.length || 0) > maxFiles;
                    if (isExceeded) {
                        onExceedFileLimitDrop?.(acceptedFiles);
                        return;
                    }
                }

                const newFiles = hasFileLimit
                    ? [...files, ...acceptedFiles].slice(0, maxFiles)
                    : [...files, ...acceptedFiles];
                onDrop?.(newFiles);
            }
        },
        [files, maxFiles, onDrop, onExceedFileLimitDrop],
    );

    const handleRemove = useCallback(
        (indexToRemove) => (e) => {
            e.stopPropagation();
            if (disabled || isReadOnly) return;
            const removedFile = files[indexToRemove];
            onRemove?.(removedFile);
        },
        [disabled, files, isReadOnly, onRemove],
    );

    const handleDownloadClick = useCallback(
        (file) => (e) => {
            e.stopPropagation();
            if (disabled) return;
            handleDownload?.(file);
        },
        [disabled, handleDownload],
    );

    const checkOverflowInternal = useCallback(() => {
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;

        const containerWidth = container.clientWidth;
        const scrollWidth = container.scrollWidth;
        const overflow = scrollWidth > containerWidth;
        const scrollLeft = container.scrollLeft;
        const maxScroll = scrollWidth - containerWidth;

        setShowPrev(overflow);
        setShowNext(overflow);
        setCanGoPrev(overflow && scrollLeft > 1);
        setCanGoNext(overflow && scrollLeft < maxScroll - 1);
    }, []);

    const checkOverflow = useThrottle(checkOverflowInternal, 100);

    const handlePrevClick = (e) => {
        e.stopPropagation();
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        const scrollAmount = container.offsetWidth * 0.8;
        container.scrollLeft = Math.max(0, container.scrollLeft - scrollAmount);
    };

    const handleNextClick = (e) => {
        e.stopPropagation();
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        const scrollAmount = container.offsetWidth * 0.8;
        container.scrollLeft = container.scrollLeft + scrollAmount;
    };

    // ========================================
    // CUSTOM HOOKS
    // ========================================
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        accept: convertAcceptToDropzoneFormat(accept),
        disabled: disabled || isReadOnly,
        maxSize,
        minSize,
        multiple: multiple ?? maxFiles > 1,
        onDrop: handleDrop,
        noClick: true,
        noKeyboard: true,
    });

    const rootProps = getRootProps({ className: classes.contentArea });

    const setScrollRef = useCallback((node) => {
        scrollContainerRef.current = node;
    }, []);

    const showDragzone = files.length === 0 || isDragActive;

    // ========================================
    // EFFECTS
    // ========================================
    useEffect(() => {
        checkOverflowInternal();
        const container = scrollContainerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });
        resizeObserver.observe(container);
        if (dropZoneContainerRef.current) {
            resizeObserver.observe(dropZoneContainerRef.current);
        }

        const handleScroll = () => {
            checkOverflow();
        };
        container.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            resizeObserver.disconnect();
            container.removeEventListener('scroll', handleScroll);
        };
    }, [files, checkOverflow, checkOverflowInternal]);

    return (
        <InputWrapper
            className={rootClassName}
            error={error}
            hint={hint}
            info={info}
            isFullWidth={isFullWidth}
            isRequired={isRequired}
            label={label}
            labelMode={labelMode}
            overrides={overridesProp}
            {...override.InputWrapper}
        >
            <div {...rootProps}>
                <input {...getInputProps({ id, name })} />
                <div className={classes.scrollWrapper}>
                    <div
                        className={classNames(classes.navArrowBase, classes.navPrev, {
                            [classes.navArrowShow]: showPrev,
                            [classes.navBtnDisabled]: !canGoPrev,
                        })}
                        onClick={canGoPrev ? handlePrevClick : undefined}
                    >
                        <Icon name="chevronLeft" className={classes.navArrowIcon} />
                    </div>
                    <div ref={setScrollRef} className={classes.scrollContainer}>
                        <div
                            ref={dropZoneContainerRef}
                            className={classes.dropZoneContainer}
                            style={{ width: contentWidth }}
                        >
                            {files.length > 0 && (
                                <>
                                    {processedFiles.map((file, index) => {
                                        const isImagePreview =
                                            previewImages &&
                                            file.fileType === 'img' &&
                                            file.preview &&
                                            checkBrowserCanRender(file.fileName || file.preview);

                                        return (
                                            <div
                                                key={`${file.name}-${index}`}
                                                className={classes.fileContainer}
                                            >
                                                {isImagePreview ? (
                                                    <img
                                                        src={file.preview}
                                                        alt={file.fileName}
                                                        className={classes.fileImagePreview}
                                                    />
                                                ) : (
                                                    <Icon
                                                        name={file.fileType}
                                                        color={getFileIconColor(
                                                            theme,
                                                            file.fileType,
                                                        )}
                                                        {...override.Icon}
                                                    />
                                                )}
                                                <div className={classes.fileInfo}>
                                                    <div className={classes.fileNameContainer}>
                                                        <Text
                                                            type="caption"
                                                            className={classes.fileName}
                                                            color={theme.colors.utility.textPrimary}
                                                            {...override.Text}
                                                        >
                                                            {file.fileName}
                                                        </Text>
                                                        {file.ext && (
                                                            <Text
                                                                type="caption"
                                                                className={classes.fileExtension}
                                                                color={
                                                                    theme.colors.utility.textPrimary
                                                                }
                                                                {...override.Text}
                                                            >
                                                                {file.ext}
                                                            </Text>
                                                        )}
                                                    </div>
                                                    {file.size > 0 && (
                                                        <Text
                                                            className={classes.fileSize}
                                                            type="caption"
                                                            color={
                                                                theme.colors.utility.textSecondary
                                                            }
                                                            {...override.Text}
                                                        >
                                                            {prettyBytes(parseInt(file.size, 10))}
                                                        </Text>
                                                    )}
                                                </div>
                                                <div className={classes.fileActions}>
                                                    <Tooltip
                                                        title={deleteTooltip}
                                                        {...override.Tooltip}
                                                    >
                                                        <div
                                                            className={classNames(
                                                                classes.actionButton,
                                                                classes.removeButton,
                                                                {
                                                                    [classes.actionButtonDisabled]:
                                                                        disabled || isReadOnly,
                                                                },
                                                            )}
                                                            onClick={handleRemove(index)}
                                                        >
                                                            {isUploading ? (
                                                                <Loader
                                                                    color="actionMinor"
                                                                    size="tiny"
                                                                    {...override.Loader}
                                                                />
                                                            ) : (
                                                                <Icon
                                                                    name="close"
                                                                    color={
                                                                        theme.colors.utility
                                                                            .textSecondary
                                                                    }
                                                                    size="small"
                                                                    {...override.Icon}
                                                                />
                                                            )}
                                                        </div>
                                                    </Tooltip>
                                                    {handleDownload && (
                                                        <Tooltip
                                                            title={downloadTooltip}
                                                            {...override.Tooltip}
                                                        >
                                                            <div
                                                                className={classNames(
                                                                    classes.actionButton,
                                                                    classes.downloadButton,
                                                                    {
                                                                        [classes.actionButtonDisabled]:
                                                                            disabled,
                                                                    },
                                                                )}
                                                                onClick={handleDownloadClick(file)}
                                                            >
                                                                <Icon
                                                                    name="download"
                                                                    color={
                                                                        theme.colors.utility
                                                                            .textSecondary
                                                                    }
                                                                    size="small"
                                                                    {...override.Icon}
                                                                />
                                                            </div>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>
                    <div
                        className={classNames(classes.navArrowBase, classes.navNext, {
                            [classes.navArrowShow]: showNext,
                            [classes.navBtnDisabled]: !canGoNext,
                        })}
                        onClick={canGoNext ? handleNextClick : undefined}
                    >
                        <Icon name="chevronRight" className={classes.navArrowIcon} />
                    </div>
                </div>
                {showDragzone && (
                    <div
                        className={classNames(classes.dropZone, {
                            [classes.dropZoneActive]: isDragActive && !disabled && !isReadOnly,
                            [classes.dropZoneInactive]: !isDragActive && !disabled && !isReadOnly,
                            [classes.dropZoneError]: error,
                            [classes.dropZoneDisabled]: disabled || isReadOnly,
                        })}
                        onClick={disabled || isReadOnly ? undefined : open}
                    >
                        {showIcon && (
                            <div className={classes.iconContainer}>
                                <Icon
                                    name="cloudUpload"
                                    color={
                                        disabled || isReadOnly
                                            ? theme.colors.grey[400]
                                            : theme.colors.actionMinor[500]
                                    }
                                    size="large"
                                    {...override.Icon}
                                />
                            </div>
                        )}
                        <Markdown
                            className={classes.title}
                            content={title}
                            overrides={markdownOverrides}
                            {...override.Markdown}
                        />
                    </div>
                )}
            </div>
        </InputWrapper>
    );
};

AttachmentList.overrides = ['Icon', 'InputWrapper', 'Loader', 'Markdown', 'Text', 'Tooltip'];

AttachmentList.propTypes = {
    /** Set accepted file types. Array of MIME types or file extensions that the component will accept. */
    accept: PropTypes.array,
    /** JSS class overrides for component styling */
    classes: PropTypes.object,
    /** Additional class name for the root element */
    className: PropTypes.string,
    /** Tooltip text for the delete button */
    deleteTooltip: PropTypes.string,
    /** When true, disables the file picker. User cannot select or drop files. */
    disabled: PropTypes.bool,
    /** Tooltip text for the download button */
    downloadTooltip: PropTypes.string,
    /** Error message displayed below the component, also triggers error styling when present */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /** Array of file objects */
    files: PropTypes.array,
    /** Callback function called when a file download is requested */
    handleDownload: PropTypes.func,
    /** Tooltip text displayed in an info icon next to the label */
    hint: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** HTML id attribute for the input element */
    id: PropTypes.string,
    /** Informational message displayed below the component */
    info: PropTypes.string,
    /** When true, component will take up 100% of the available width */
    isFullWidth: PropTypes.bool,
    /** When true, prevents user from selecting or removing files. The component appears non-interactive. */
    isReadOnly: PropTypes.bool,
    /** When true, displays an asterisk next to the label indicating the field is required */
    isRequired: PropTypes.bool,
    /** Show loading state on file items */
    isUploading: PropTypes.bool,
    /** Text to be displayed as the field's label */
    label: PropTypes.string,
    /** Determines how the label is positioned relative to the component */
    labelMode: PropTypes.oneOf(['horizontal', 'vertical']),
    /** Maximum number of files allowed */
    maxFiles: PropTypes.number,
    /** Maximum file size in bytes. Files larger than this will be rejected. */
    maxSize: PropTypes.number,
    /** Minimum file size in bytes. Files smaller than this will be rejected. */
    minSize: PropTypes.number,
    /** When true, allows selecting multiple files at once. Defaults to true if maxFiles > 1. */
    multiple: PropTypes.bool,
    /** HTML name attribute for the input element */
    name: PropTypes.string,
    /** Callback when files are added or removed. Receives the new array of files. */
    onDrop: PropTypes.func,
    /** Function called when dropped files exceed the maxFiles limit. Receives the array of dropped files. */
    onExceedFileLimitDrop: PropTypes.func,
    /** Function called when a file is removed. Receives the file being removed. */
    onRemove: PropTypes.func,
    /** Override props for child components */
    overrides: PropTypes.object,
    /** When true, displays a preview of selected image files */
    previewImages: PropTypes.bool,
    /** When true, displays the upload icon in the dropzone */
    showIcon: PropTypes.bool,
    /** Main title text displayed in the dropzone. Default is 'Drag & drop files here'. */
    title: PropTypes.string,
};

const MemoizedAttachmentList = memo(AttachmentList);
MemoizedAttachmentList.displayName = 'AttachmentList';

export default MemoizedAttachmentList;
